import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

const NEEDS_OPTIONS = [
  { id: 'life_insurance', label: 'Life Insurance' },
  { id: 'will_trust', label: 'Will / Trust' },
  { id: 'child_education', label: 'Child Education Planning' },
  { id: 'retirement', label: 'Retirement Planning' },
];

export function DashboardFlyer({ className }) {
  const { user } = useAuth();
  const [flyer, setFlyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    needs: [],
    notes: ''
  });
  const [errors, setErrors] = useState({});

  // Pre-fill user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        email: user.email || ''
      }));
    }
  }, [user]);

  // Fetch active flyer
  useEffect(() => {
    const fetchFlyer = async () => {
      try {
        const backendUrl = getBackendUrl();
        const response = await fetch(`${backendUrl}/api/flyer/active`);
        
        if (response.ok) {
          const data = await response.json();
          setFlyer(data.flyer);
        }
      } catch (error) {
        console.error('Error fetching flyer:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFlyer();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.needs.length === 0) {
      newErrors.needs = 'Please select at least one option';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/financial-planning/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit request');
      }
      
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNeedToggle = (needId) => {
    setFormData(prev => ({
      ...prev,
      needs: prev.needs.includes(needId)
        ? prev.needs.filter(n => n !== needId)
        : [...prev.needs, needId]
    }));
    // Clear needs error when user selects something
    if (errors.needs) {
      setErrors(prev => ({ ...prev, needs: undefined }));
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    // Reset form if not submitted
    if (!submitted) {
      setFormData(prev => ({
        ...prev,
        needs: [],
        notes: ''
      }));
      setErrors({});
    }
  };

  const resetAndClose = () => {
    setSubmitted(false);
    setModalOpen(false);
    setFormData(prev => ({
      ...prev,
      needs: [],
      notes: ''
    }));
    setErrors({});
  };

  // Don't render if loading or no active flyer
  if (loading || !flyer || !flyer.is_active) {
    return null;
  }

  return (
    <>
      {/* Flyer Image Tile */}
      <div
        className={cn(
          "relative cursor-pointer group overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all duration-300",
          "transform hover:scale-[1.02]",
          className
        )}
        onClick={() => setModalOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setModalOpen(true);
          }
        }}
        data-testid="dashboard-flyer-tile"
      >
        <img
          src={flyer.image_url}
          alt="Financial planning flyer. Click to submit a request."
          className="h-[150px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        {/* Subtle hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
      </div>

      {/* Request Modal */}
      <Dialog open={modalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          {submitted ? (
            // Success State
            <div className="py-8 text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <DialogHeader className="text-center">
                <DialogTitle className="text-xl">Thank you!</DialogTitle>
                <DialogDescription className="mt-2">
                  We received your request. We'll get back to you shortly.
                </DialogDescription>
              </DialogHeader>
              <Button 
                className="mt-6" 
                onClick={resetAndClose}
                data-testid="flyer-modal-close-success"
              >
                Close
              </Button>
            </div>
          ) : (
            // Form State
            <>
              <DialogHeader>
                <DialogTitle>Tell Us What You Need</DialogTitle>
                <DialogDescription>
                  Share a few details. Our team will review and get back to you shortly.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="full_name">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Enter your full name"
                    className={cn(errors.full_name && "border-red-500")}
                    data-testid="flyer-input-name"
                  />
                  {errors.full_name && (
                    <p className="text-sm text-red-500">{errors.full_name}</p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    className={cn(errors.email && "border-red-500")}
                    data-testid="flyer-input-email"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    data-testid="flyer-input-phone"
                  />
                </div>

                {/* Needs Checkboxes */}
                <div className="space-y-3">
                  <Label>
                    What do you need? <span className="text-red-500">*</span>
                  </Label>
                  <div className="grid grid-cols-1 gap-3">
                    {NEEDS_OPTIONS.map((option) => (
                      <div key={option.id} className="flex items-center space-x-3">
                        <Checkbox
                          id={option.id}
                          checked={formData.needs.includes(option.id)}
                          onCheckedChange={() => handleNeedToggle(option.id)}
                          data-testid={`flyer-checkbox-${option.id}`}
                        />
                        <Label 
                          htmlFor={option.id} 
                          className="text-sm font-normal cursor-pointer"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {errors.needs && (
                    <p className="text-sm text-red-500">{errors.needs}</p>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any specific questions or requirements..."
                    rows={3}
                    data-testid="flyer-input-notes"
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleCloseModal}
                    data-testid="flyer-modal-close"
                  >
                    Close
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    data-testid="flyer-modal-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
